import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Container, Typography, Grid, IconButton } from "@mui/material";
import { ArrowBack, PlayCircleOutline } from "@mui/icons-material";
import { API_URL } from "../config";
import ExamBeginAlertPage from "../common/alert/ExamBeginAlertpage";

const TestLevelsPage = () => {
  const navigate = useNavigate();
  const { examId, subjectId } = useParams();

  const [tests, setTests] = useState([]);
  const [examImageUri, setExamImageUri] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExamAlert, setShowExamAlert] = useState(false);
  const [selectedExamTestId, setSelectedExamTestId] = useState(null);

  const containerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const tokenString = localStorage.getItem("authToken");
      if (!tokenString) throw new Error("No auth token");

      const token = JSON.parse(tokenString);

      const [testsRes, examTestRes, imgRes] = await Promise.all([
        axios.get(`${API_URL}/exams/${examId}/subjects/${subjectId}/tests`, {
          headers: { Authorization: `Bearer ${token.access}` },
        }),
        axios.get(
          `${API_URL}/main/exams/${examId}/subjects/${subjectId}/tests`,
          {
            headers: { Authorization: `Bearer ${token.access}` },
          }
        ),
        axios.get(
          `${API_URL}/main/exams/img/${examId}/subjects/${subjectId}/tests`,
          {
            headers: { Authorization: `Bearer ${token.access}` },
          }
        ),
      ]);

      const normalTests = testsRes.data;
      const examTests = examTestRes.data.map((t) => ({
        ...t,
        isExamTest: true,
      }));

      // Insert exam test every 10 normal tests
      const combined = [];
      normalTests.forEach((test, index) => {
        combined.push(test);
        if ((index + 1) % 10 === 0 && index + 1 < normalTests.length) {
          const examIndex = Math.floor(index / 10);
          if (examTests[examIndex]) combined.push(examTests[examIndex]);
        }
      });

      setTests(combined);
      setExamImageUri(imgRes.data.exam_image_url);

      // Auto-scroll to first locked or last unlocked
      setTimeout(() => {
        const firstLocked = combined.findIndex((t) => t.lock);
        const lastUnlocked = combined
          .map((t, i) => (t.lock ? -1 : i))
          .filter((i) => i >= 0)
          .pop();
        const target = firstLocked !== -1 ? firstLocked : lastUnlocked;

        if (target !== undefined && target >= 0 && containerRef.current) {
          const element = containerRef.current.children[target];
          element?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 300);
    } catch (err) {
      console.error(err);
      setError("Failed to load levels");
    } finally {
      setLoading(false);
    }
  }, [examId, subjectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTestClick = (test) => {
    if (test.lock) return;

    if (test.isExamTest) {
      setSelectedExamTestId(test.id);
      setShowExamAlert(true);
    } else {
      navigate(`/questions/${examId}/${subjectId}/${test.id}`);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f8f7f4",
        fontFamily: "'Sora', sans-serif",
        backgroundImage:
          "linear-gradient(rgba(99,60,180,0.035) 1px,transparent 1px),linear-gradient(90deg,rgba(99,60,180,0.035) 1px,transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          position: "relative",
          bgcolor: "#fff",
          borderBottom: "1px solid rgba(26,26,46,0.07)",
          px: { xs: 2.5, md: 4 },
          pt: "3px", // room for accent bar
          pb: 0,
        }}
      >
        {/* Animated top stripe */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background:
              "linear-gradient(90deg,#633cb4,#a855f7,#e879f9,#633cb4)",
            backgroundSize: "300% 100%",
            animation: "stripe 4s linear infinite",
            "@keyframes stripe": { to: { backgroundPosition: "-300% 0" } },
          }}
        />

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              flexShrink: 0,
              border: "1.5px solid rgba(99,60,180,0.2)",
              bgcolor: "rgba(99,60,180,0.05)",
              color: "#633cb4",
              "&:hover": { bgcolor: "rgba(99,60,180,0.12)" },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>

          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: "1.35rem",
                fontWeight: 700,
                color: "#1a1a2e",
                letterSpacing: "-0.02em",
                mb: 0.25,
              }}
            >
              Practice Levels
            </Typography>
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.68rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(26,26,46,0.38)",
              }}
            >
              Master one level at a time
            </Typography>
          </Box>

          {/* Level count badge */}
          <Box
            sx={{
              background:
                "linear-gradient(135deg,rgba(99,60,180,0.08),rgba(168,85,247,0.06))",
              border: "1px solid rgba(99,60,180,0.15)",
              borderRadius: "10px",
              px: 1.75,
              py: 1,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "1.1rem",
                fontWeight: 700,
                color: "#633cb4",
                display: "block",
              }}
            >
              {tests.length}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.62rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(99,60,180,0.5)",
              }}
            >
              Levels
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── BODY ── */}
      <Container maxWidth="xl" sx={{ py: 3.5, px: { xs: 2, md: 3 } }}>
        {/* Loading */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 12,
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                border: "2.5px solid rgba(99,60,180,0.12)",
                borderTopColor: "#a855f7",
                animation: "spin 0.9s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
              }}
            />
            <Typography
              sx={{
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.7rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "rgba(99,60,180,0.45)",
                animation: "blink 1.5s ease-in-out infinite",
                "@keyframes blink": {
                  "0%,100%": { opacity: 0.35 },
                  "50%": { opacity: 1 },
                },
              }}
            >
              Loading levels
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              bgcolor: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.2)",
              color: "#dc2626",
              fontSize: "0.88rem",
            }}
          >
            {error}
          </Box>
        )}

        {/* Section label */}
        {!loading && !error && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              {/* All Levels */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#633cb4",
                  }}
                />

                <Typography
                  sx={{
                    fontFamily: "'JetBrains Mono',monospace",
                    fontSize: "0.67rem",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#633cb4",
                    fontWeight: 500,
                  }}
                >
                  All Levels
                </Typography>
              </Box>

              {/* Video Lessons */}
              <Box
                component="button"
                onClick={() =>
                  navigate(`/subjects/${subjectId}/exams/${examId}/videos`)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  border: "1px solid rgba(99,60,180,0.2)",
                  borderRadius: "10px",
                  background: "rgba(99,60,180,0.06)",
                  color: "#633cb4",
                  px: 1.5,
                  py: 0.8,
                  cursor: "pointer",
                  fontFamily: "'Sora', sans-serif",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  transition: "all 0.2s ease",

                  "&:hover": {
                    background: "rgba(99,60,180,0.12)",
                    borderColor: "rgba(99,60,180,0.35)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(99,60,180,0.12)",
                  },
                }}
              >
                <PlayCircleOutline sx={{ fontSize: 19 }} />

                Video Lessons
              </Box>
            </Box>

            {/* GRID */}
            <Grid
              container
              spacing={{ xs: 1.5, sm: 2 }}
              ref={containerRef}
              alignItems="stretch"
            >
              {tests.map((test, index) => (
                <Grid
                  item
                  key={test.id ?? index}
                  xs={6}
                  sm={4}
                  md={3}
                  lg={2}
                  sx={{ display: "flex" }}
                >
                  <Box
                    onClick={() => !test.lock && handleTestClick(test)}
                    sx={{
                      width: "100%",
                      bgcolor: test.isExamTest
                        ? "linear-gradient(135deg,#fff9ec,#fff3d4)"
                        : test.lock
                        ? "#fafafa"
                        : "#fff",
                      background: test.isExamTest
                        ? "linear-gradient(135deg,#fff9ec,#fff3d4)"
                        : test.lock
                        ? "#fafafa"
                        : "#fff",
                      border: "1.5px solid",
                      borderColor: test.isExamTest
                        ? "rgba(249,115,22,0.35)"
                        : test.lock
                        ? "rgba(26,26,46,0.08)"
                        : "rgba(26,26,46,0.09)",
                      borderRadius: "16px",
                      p: "20px 14px 16px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.25,
                      cursor: test.lock ? "default" : "pointer",
                      opacity: test.lock ? 0.55 : 1,
                      position: "relative",
                      overflow: "hidden",
                      textAlign: "center",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                      animation: `cardIn 0.4s ${index * 0.04}s ease both`,
                      "@keyframes cardIn": {
                        from: {
                          opacity: 0,
                          transform: "translateY(14px) scale(0.96)",
                        },
                        to: { opacity: 1, transform: "translateY(0) scale(1)" },
                      },
                      "&:hover": !test.lock
                        ? {
                            transform: "translateY(-6px)",
                            boxShadow: test.isExamTest
                              ? "0 12px 32px rgba(249,115,22,0.2)"
                              : "0 12px 32px rgba(99,60,180,0.13)",
                            borderColor: test.isExamTest
                              ? "#f97316"
                              : "#a855f7",
                          }
                        : {},
                    }}
                  >
                    {/* Exam radial glow */}
                    {test.isExamTest && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "radial-gradient(ellipse at 50% 0%,rgba(249,115,22,0.08),transparent 70%)",
                          pointerEvents: "none",
                        }}
                      />
                    )}

                    {/* Lock pip */}
                    {test.lock && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          fontSize: "12px",
                          opacity: 0.4,
                        }}
                      >
                        🔒
                      </Box>
                    )}

                    {/* Icon wrap */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "1.6rem",
                        background: test.isExamTest
                          ? "linear-gradient(135deg,#ffedd5,#fed7aa)"
                          : test.lock
                          ? "rgba(26,26,46,0.05)"
                          : "linear-gradient(135deg,#ede9fe,#ddd6fe)",
                        border: "1.5px solid",
                        borderColor: test.isExamTest
                          ? "rgba(249,115,22,0.25)"
                          : test.lock
                          ? "rgba(26,26,46,0.1)"
                          : "rgba(99,60,180,0.2)",
                        transition: "transform 0.25s",
                        "&:hover": !test.lock
                          ? { transform: "scale(1.08)" }
                          : {},
                      }}
                    >
                      {test.isExamTest ? "🏆" : test.lock ? "🔒" : "⭐"}
                    </Box>

                    {/* Text */}
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: "0.63rem",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "rgba(26,26,46,0.35)",
                          mb: 0.25,
                        }}
                      >
                        {test.isExamTest
                          ? "EXAM"
                          : `LVL ${String(test.level).padStart(2, "0")}`}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: "0.92rem",
                          fontWeight: 700,
                          color: test.isExamTest ? "#7c2d12" : "#1a1a2e",
                          lineHeight: 1.2,
                        }}
                      >
                        {test.isExamTest
                          ? `Exam ${test.level}`
                          : `Level ${test.level}`}
                      </Typography>
                    </Box>

                    {/* Status chip */}
                    <Box
                      sx={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        px: 1.25,
                        py: 0.4,
                        borderRadius: "99px",
                        border: "1px solid",
                        ...(test.isExamTest
                          ? {
                              bgcolor: "rgba(249,115,22,0.1)",
                              borderColor: "rgba(249,115,22,0.3)",
                              color: "#c2410c",
                            }
                          : test.lock
                          ? {
                              bgcolor: "rgba(26,26,46,0.05)",
                              borderColor: "rgba(26,26,46,0.12)",
                              color: "rgba(26,26,46,0.4)",
                            }
                          : {
                              bgcolor: "rgba(34,197,94,0.08)",
                              borderColor: "rgba(34,197,94,0.25)",
                              color: "#15803d",
                            }),
                      }}
                    >
                      {test.isExamTest
                        ? "Special"
                        : test.lock
                        ? "Locked"
                        : "Ready"}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>

      <ExamBeginAlertPage
        visible={showExamAlert}
        imageSource={examImageUri}
        title="CONGRATULATIONS!"
        onClose={() => {
          setShowExamAlert(false);
          if (selectedExamTestId)
            navigate(`/Exam/${examId}/${subjectId}/${selectedExamTestId}`);
        }}
        onCancel={() => setShowExamAlert(false)}
      />
    </Box>
  );
};

export default TestLevelsPage;
